import { cn } from '@polyseme/utils/cn/cn-utils';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from 'lucide-react';

import { Button } from '../../ui';

type PaginationItemProps = {
  isActive?: boolean;
  onClick?: () => void;
  page: number;
};

const PaginationItem = ({ isActive, onClick, page }: PaginationItemProps) => (
  <Button
    aria-current={isActive ? 'page' : undefined}
    aria-label={`Page ${page}`}
    className="size-8"
    size="sm"
    variant={isActive ? 'outline' : 'ghost'}
    onClick={onClick}
  >
    {page}
  </Button>
);

type PaginationLabelProps = Omit<PaginationItemProps, 'page'> & {
  direction: 'next' | 'previous';
  label: string;
};

const PaginationLabel = ({
  direction,
  isActive,
  label,
  onClick,
}: PaginationLabelProps) => {
  const Icon = direction === 'previous' ? ChevronLeftIcon : ChevronRightIcon;

  return (
    <Button
      className={cn(
        'h-8 w-auto gap-1',
        direction === 'previous' ? 'flex-row' : 'flex-row-reverse'
      )}
      disabled={!isActive}
      size="sm"
      variant="ghost"
      onClick={onClick}
    >
      <Icon size={16} />
      <span>{label}</span>
    </Button>
  );
};

type Props = {
  currentPage: number;
  labels?: {
    next: string;
    previous: string;
  };
  setPageIndex: (pageIndex: number) => void;
  totalPages: number;
};

export const Pagination = ({
  currentPage,
  labels,
  setPageIndex,
  totalPages,
}: Props) => {
  return (
    <>
      {!!labels && (
        <PaginationLabel
          direction="previous"
          isActive={currentPage > 0}
          label={labels.previous}
          onClick={() => {
            if (currentPage > 0) {
              setPageIndex(currentPage - 1);
            }
          }}
        />
      )}
      {/* First Page */}
      {currentPage > 1 && (
        <>
          <PaginationItem page={1} onClick={() => setPageIndex(0)} />
          {currentPage > 2 && <MoreHorizontalIcon size={16} />}
        </>
      )}
      {/* Previous Page */}
      {currentPage > 0 && (
        <PaginationItem
          page={currentPage}
          onClick={() => setPageIndex(currentPage - 1)}
        />
      )}
      {/* Current Page */}
      <PaginationItem isActive page={currentPage + 1} />
      {/* Next Page */}
      {currentPage < totalPages - 1 && (
        <PaginationItem
          page={currentPage + 2}
          onClick={() => setPageIndex(currentPage + 1)}
        />
      )}
      {/* Last Page */}
      {currentPage < totalPages - 2 && (
        <>
          {currentPage < totalPages - 3 && <MoreHorizontalIcon size={16} />}
          <PaginationItem
            page={totalPages}
            onClick={() => setPageIndex(totalPages - 1)}
          />
        </>
      )}
      {!!labels && (
        <PaginationLabel
          direction="next"
          isActive={currentPage < totalPages - 1}
          label={labels.next}
          onClick={() => {
            if (currentPage < totalPages - 1) {
              setPageIndex(currentPage + 1);
            }
          }}
        />
      )}
    </>
  );
};

type CursorPaginationProps = {
  currentPage: number;
  /** Known page count, used to bound the offered pages; undefined while loading. */
  totalPages?: number;
  /** Whether a next page is reachable (e.g. the server returned a next cursor). */
  hasNext: boolean;
  labels: {
    next: string;
    previous: string;
  };
  setPageIndex: (pageIndex: number) => void;
};

/**
 * Numbered pagination for cursor/keyset-paged tables: offers the pages around
 * the current one and a trailing ellipsis, never a last-page number (per the
 * Figma spec) — arbitrary far jumps are not addressable with cursors.
 */
export const CursorPagination = ({
  currentPage,
  totalPages,
  hasNext,
  labels,
  setPageIndex,
}: CursorPaginationProps) => {
  const lastIndex =
    totalPages === undefined ? currentPage + (hasNext ? 1 : 0) : totalPages - 1;

  return (
    <>
      <PaginationLabel
        direction="previous"
        isActive={currentPage > 0}
        label={labels.previous}
        onClick={() => {
          if (currentPage > 0) {
            setPageIndex(currentPage - 1);
          }
        }}
      />
      {/* First Page */}
      {currentPage > 1 && (
        <>
          <PaginationItem page={1} onClick={() => setPageIndex(0)} />
          {currentPage > 2 && <MoreHorizontalIcon size={16} />}
        </>
      )}
      {/* Previous Page */}
      {currentPage > 0 && (
        <PaginationItem
          page={currentPage}
          onClick={() => setPageIndex(currentPage - 1)}
        />
      )}
      {/* Current Page */}
      <PaginationItem isActive page={currentPage + 1} />
      {/* Next Pages */}
      {currentPage + 1 <= lastIndex && (
        <PaginationItem
          page={currentPage + 2}
          onClick={() => setPageIndex(currentPage + 1)}
        />
      )}
      {currentPage + 2 <= lastIndex && (
        <PaginationItem
          page={currentPage + 3}
          onClick={() => setPageIndex(currentPage + 2)}
        />
      )}
      {/* More pages exist but their numbers are not offered */}
      {currentPage + 3 <= lastIndex && <MoreHorizontalIcon size={16} />}
      <PaginationLabel
        direction="next"
        isActive={hasNext}
        label={labels.next}
        onClick={() => {
          if (hasNext) {
            setPageIndex(currentPage + 1);
          }
        }}
      />
    </>
  );
};
